import type { JSX } from 'react';
import { localState, selectState } from 'dg-web-shared/lib/Flux';
import { css } from '@emotion/css';
import {
    generateEnforcementContextualServerReadStateSlice,
    generateEnforcementServerWriteStateSlice,
} from '../state/EnforcementStateSlices';
import * as superagent from 'superagent';
import { makeEnforcementUrl } from '../Http';
import { TextButton } from './TextButton';
import { AlertBoxed, PendingOrErrorModal } from './Modals';
import { texts } from '../i18n';
import { RequestStatus } from 'dg-web-shared/lib/ServerRequestStateSlices';
import { Scanner } from '@yudiel/react-qr-scanner';
import { Language } from 'dg-web-shared/lib/Localized.ts';

namespace QrCodeDataState {
    export type Context = {
        qrCode: string;
    };

    export interface BaseResponse {
        status:
            | QrCodeStatus.unpaired
            | QrCodeStatus.qrCodeUnknown
            | QrCodeStatus.alreadyPairedPermit
            | QrCodeStatus.alreadyPairedOtherOperator;
        pairingAllowed: boolean;
    }

    export interface AlreadyPairedResponse {
        status: QrCodeStatus.alreadyPaired;
        zoneName: string;
        zoneId: number;
        pairingAllowed: boolean;
    }

    export type Response = BaseResponse | AlreadyPairedResponse;

    export enum QrCodeStatus {
        unpaired = 'unpaired',
        alreadyPaired = 'alreadyPaired',
        alreadyPairedOtherOperator = 'alreadyPairedOtherOperator',
        alreadyPairedPermit = 'alreadyPairedPermit',
        qrCodeUnknown = 'qrCodeUnknown',
    }

    export const { get, refetchSameContext } =
        generateEnforcementContextualServerReadStateSlice<Context, Response>({
            key: 'qr-code-data',
            req: context =>
                superagent
                    .get(makeEnforcementUrl('twint-qr-code'))
                    .query({ code: context.qrCode }),
        });
}

namespace PairRequestState {
    export interface Payload {
        code: string;
        zoneId: number;
    }

    export interface OkResponse {
        status: 'ok';
    }

    export const { get, send, reset } =
        generateEnforcementServerWriteStateSlice<Payload, OkResponse>({
            key: 'twint-pair-request',
            req: (payload?: Payload) =>
                superagent
                    .post(makeEnforcementUrl('twint-qr-code/pair'))
                    .send(payload),
            useSessionToken: true,
            onResponse: (store, req) => {
                if (req.status === 'success') {
                    store.update(store =>
                        QrCodeDataState.refetchSameContext(store, true),
                    );
                }
            },
        });
}

function QrCodeScannerWithLegacyMode({
    setQrCode,
}: {
    setQrCode: (data: string | null) => void;
}) {
    return (
        <div>
            <Scanner
                constraints={{ facingMode: 'environment' }}
                scanDelay={500}
                components={{ finder: false }}
                styles={{
                    container: {
                        width: '57%',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                    },
                }}
                onError={error => {
                    console.error(error);
                }}
                onScan={detectedCodes => {
                    const code = detectedCodes[0]?.rawValue ?? null;
                    if (code) {
                        setQrCode(code);
                    }
                }}
            />
        </div>
    );
}

export const QrCodeScanner = localState<
    {
        selectedZone: number | null;
        zoneName: string | null;
        language: Language;
    },
    { qrCode: string | null }
>(
    { qrCode: '' },
    selectState(
        (store, props) => {
            return {
                qrCodeData: QrCodeDataState.get(
                    store,
                    props.state.qrCode ? { qrCode: props.state.qrCode } : null,
                ),
                pairRequest: PairRequestState.get(store),
            };
        },
        (p): JSX.Element => {
            const qrCodeParts = p.state.qrCode
                ? p.state.qrCode.split('#')
                : ['-'];

            const pairingPossible =
                qrCodeParts.length > 1 &&
                p.pairRequest.status === RequestStatus.NEVER_EXECUTED &&
                p.qrCodeData.data &&
                p.qrCodeData.data.pairingAllowed &&
                (p.qrCodeData.data.status ===
                    QrCodeDataState.QrCodeStatus.unpaired ||
                    (p.qrCodeData.data.status ===
                        QrCodeDataState.QrCodeStatus.alreadyPaired &&
                        p.selectedZone !== p.qrCodeData.data.zoneId));

            return (
                <div className={css({ width: '100%' })}>
                    <PendingOrErrorModal
                        slices={[p.pairRequest, p.qrCodeData]}
                    />
                    <QrCodeScannerWithLegacyMode
                        setQrCode={(qrCode: string | null | undefined) => {
                            if (qrCode) {
                                if (qrCode !== p.state.qrCode) {
                                    p.setState({ qrCode: qrCode });
                                    p.update(PairRequestState.reset);
                                }
                            }
                        }}
                    />
                    <div
                        className={css({
                            fontFamily: 'roboto',
                            fontSize: 20,
                            margin: '16px 0',
                            textAlign: 'center',
                        })}
                    >
                        {texts[p.language].qrCode}:{' '}
                        {qrCodeParts[qrCodeParts.length - 1]}
                    </div>
                    <div
                        className={css({
                            fontFamily: 'roboto',
                            margin: '16px 30px',
                            textAlign: 'center',
                        })}
                    >
                        <p>
                            {p.qrCodeData.status === RequestStatus.SUCCESS &&
                                qrCodeStatusTexts(
                                    p.qrCodeData.data,
                                    p.selectedZone,
                                    p.language,
                                )}
                        </p>
                    </div>
                    <div
                        className={css({
                            fontFamily: 'roboto',
                            fontSize: 20,
                            margin: '16px 0',
                            textAlign: 'center',
                        })}
                    >
                        <p>
                            {p.qrCodeData.status === RequestStatus.SUCCESS &&
                                ((): string => {
                                    switch (p.qrCodeData.data.status) {
                                        case QrCodeDataState.QrCodeStatus
                                            .alreadyPaired:
                                            return p.selectedZone !==
                                                p.qrCodeData.data.zoneId
                                                ? p.qrCodeData.data.zoneName
                                                : '';
                                        default:
                                            return '';
                                    }
                                })()}
                        </p>
                    </div>
                    <div
                        className={css({
                            display: 'flex',
                            margin: '16px 0',
                            justifyContent: 'center',
                        })}
                    >
                        {pairingPossible && (
                            <TextButton
                                label={texts[p.language].cancel}
                                onClick={() => {
                                    p.update(PairRequestState.reset);
                                    p.setState({ qrCode: null });
                                }}
                            />
                        )}
                        {pairingPossible && (
                            <AlertBoxed
                                confirmLabel={texts[p.language].confirm}
                                confirmColor={'blue'}
                                onConfirm={() => {
                                    p.update(PairRequestState.send, {
                                        code: p.state.qrCode || '',
                                        zoneId: p.selectedZone || -1,
                                    });
                                    p.setState({ qrCode: null });
                                }}
                                trigger={
                                    <TextButton
                                        label={
                                            p.qrCodeData.data &&
                                            p.qrCodeData.data.status ===
                                                QrCodeDataState.QrCodeStatus
                                                    .alreadyPaired
                                                ? texts[p.language]
                                                      .overrideQrCode
                                                : texts[p.language].pairQrCode
                                        }
                                        onClick={() => null}
                                    />
                                }
                            >
                                <p>
                                    {p.qrCodeData.data &&
                                    p.qrCodeData.data.status ===
                                        QrCodeDataState.QrCodeStatus
                                            .alreadyPaired
                                        ? texts[p.language].attention
                                        : ''}
                                </p>
                                <p>
                                    {p.qrCodeData.data &&
                                    p.qrCodeData.data.status ===
                                        QrCodeDataState.QrCodeStatus
                                            .alreadyPaired
                                        ? texts[p.language].overrideQrCodeInfo
                                        : texts[p.language].confirmPair}
                                </p>
                                <p>
                                    <strong>{p.zoneName || ''}</strong>
                                </p>
                            </AlertBoxed>
                        )}
                    </div>
                </div>
            );
        },
    ),
);

export function qrCodeStatusTexts(
    qrCodeData: QrCodeDataState.Response,
    selectedZone: number | null,
    language: Language,
) {
    switch (qrCodeData.status) {
        case QrCodeDataState.QrCodeStatus.unpaired:
            return texts[language].unpaired;
        case QrCodeDataState.QrCodeStatus.alreadyPaired:
            return qrCodeData.zoneId !== selectedZone
                ? texts[language].alreadyPaired
                : texts[language].alreadyPairedCurrentZone;
        case QrCodeDataState.QrCodeStatus.alreadyPairedPermit:
            return texts[language].alreadyPairedPermit;
        case QrCodeDataState.QrCodeStatus.alreadyPairedOtherOperator:
            return texts[language].alreadyPairedOtherOperator;
        case QrCodeDataState.QrCodeStatus.qrCodeUnknown:
            return texts[language].unknownQrCode;
    }
}
